import {
  createTranslationMessages,
  createTranslationRef,
} from '@backstage/frontend-plugin-api';
import { TranslationBlueprint } from '@backstage/plugin-app-react';

/**
 * Default (English) messages of the « Coûts » card of an Odoo project
 * (CostsCard.tsx) and of the admin page « Coûts plateforme »
 * (CostsPage.tsx). One register for both: the card and the page say
 * « Complet », « Plateforme », « Capacité libre » with the same words.
 *
 * Same arrangement as `components/projectEnvironments/translation.ts`: the
 * French resource below overrides the default language, with `full: true`,
 * and is registered by a module on the `app` plugin (`plugin.tsx`).
 */
export const projectCostsTranslationRef = createTranslationRef({
  id: 'waadoo-project-costs',
  messages: {
    // The BCP 47 locale of the amounts, shares and dates — see
    // `format.locale` in the environments translation for why it is a
    // message.
    'format.locale': 'en',
    // « ≈ 115,02 € »: an end-of-month figure, a projection.
    'format.approx': '≈ {{amount}}',
    'card.title': 'Costs',
    'card.subtitle': 'Actual cost of the project, split from the OVH bill',
    'card.months': 'Month',
    'card.view': 'Costs view',
    'card.viewMonth': 'This month',
    'card.viewHistory': 'History',
    'card.empty': 'No costs for this project yet',
    'card.emptyHint':
      'Costs are collected every hour: they appear once a collection cycle has covered the project.',
    'card.error': 'The costs could not be read.',
    // The chevron of the header (folded by default, 2026-10-29).
    'card.showDetails': 'Show the details',
    'card.hideDetails': 'Hide the details',
    // The folded header's key figures of the month (choice A, 2026-10-29):
    // « 4,61 € / à ce jour · dont direct 0,58 € », « ≈ 9,96 € / projection
    // fin de mois », the bars « par jour ».
    'fold.label': 'Key figures of the month',
    'fold.toDate': 'to date',
    'fold.ofMonth': 'for the month',
    'fold.projection': 'end-of-month projection',
    // On a phone, after the projected amount, on the folded card's line.
    'fold.projected': 'projected',
    'fold.days': 'per day',
    'card.footnote':
      '“Platform”: share of the common costs (cluster, load balancers, tools, backups). “Free capacity”: unused share of the pool’s nodes. Both are included in the full cost, not in the direct cost.',
    // On a phone, the footnote's one line, and its « ⓘ » button, which
    // shows the footnote itself (and the projection's method, while the
    // month is projected: `card.projectionNote`).
    'card.footnoteShort': 'Platform and free capacity: shared costs',
    'card.footnoteMore': 'About the shared costs',
    'card.projectionNote': 'End-of-month projection: {{method}}.',
    // The month's state, next to the month picker.
    'status.open': 'Open month',
    'status.closed': 'Closed · reconciled with the bill',
    // The same, in the history table.
    'status.historyOpen': 'Open · provisional',
    // On a phone, next to the month picker and on the history's lines.
    'status.openShort': 'open',
    'status.closedShort': 'closed',
    'strip.label': 'Month summary',
    'strip.complete': 'Full cost to date',
    'strip.completeClosed': 'Full cost of the month',
    'strip.completeSub': 'including direct {{direct}}',
    'strip.since': 'since {{date}}',
    'strip.projection': 'End-of-month projection',
    'strip.estimate': 'estimate',
    // On a phone, in the projection's half-width tile.
    'strip.estimateShort': 'est.',
    'strip.projectionShare':
      'project share {{share}} × OVH forecast {{forecast}}',
    'strip.projectionLinear': 'pro rata of the hours covered',
    'strip.projectionClosed': 'Closed month',
    'strip.coverage': 'Coverage',
    'strip.coverageValue': '{{covered}} h / {{total}} h',
    'strip.coverageSub': 'hours of the month already measured',
    'items.title': 'By item',
    'item.cpu': 'CPU',
    'item.ram': 'Memory',
    'item.volume': 'Volume',
    'item.nfs': 'NFS share',
    'item.s3': 'S3 storage',
    'item.idle': 'Free capacity',
    'item.platform': 'Platform',
    // On a phone, the items under 2 % of the month, on one legend line.
    'items.others': 'Others ({{names}})',
    'environments.titleOne': 'Environment',
    'environments.title': 'By environment',
    'environments.only': 'only environment · {{amount}}',
    'environments.item': 'Item',
    'environments.total': 'Total',
    'environments.complete': 'Full',
    'days.title': 'By day',
    'days.label': 'Full cost per day, {{month}}',
    'days.bar': '{{day}}: {{amount}}',
    'days.error': 'The daily costs could not be read.',
    'history.label': 'Full cost per month',
    'history.month': 'Month',
    'history.complete': 'Full',
    'history.direct': 'Direct',
    'history.status': 'Status',
    'history.error': 'The history could not be read.',
    // The admin page.
    'page.title': 'Platform costs',
    'page.subtitle':
      'OVH costs of the platform, split by project (administrators).',
    'page.months': 'Month',
    'page.previousMonth': 'Previous month',
    'page.nextMonth': 'Next month',
    'page.statusOpen': 'Open month',
    // The same on a phone, next to the month: the word alone.
    'page.statusOpenShort': 'open',
    'page.statusClosed': 'Closed',
    'page.closeFrom': 'can be closed from {{date}}',
    'page.export': 'Export CSV',
    'page.error': 'The platform costs could not be read.',
    'page.noMonth': 'No month has costs yet.',
    'kpi.ovhTotal': 'OVH total of the month (cycles)',
    'kpi.ovhTotalSub': 'reconciled hour by hour',
    'kpi.distributed': 'Split over the projects',
    'kpi.unattributed': 'Unattributed',
    'kpi.unattributedSub': '{{share}} · alert above 2%',
    'kpi.forecast': 'OVH end-of-month forecast',
    'kpi.forecastSub': 'published by OVH (usage/forecast)',
    'kpi.coverage': 'Coverage',
    'kpi.coverageSub': 'collected since {{date}}',
    'projects.title': 'Projects',
    'projects.subtitle_one':
      '{{count}} project · full cost = direct + free capacity + platform',
    'projects.subtitle_other':
      '{{count}} projects · full cost = direct + free capacity + platform',
    'projects.project': 'Project',
    'projects.environments': 'Environments',
    'projects.complete': 'Full',
    'projects.direct': 'Direct',
    'projects.idle': 'Free',
    'projects.platform': 'Platform',
    'projects.share': 'Share',
    'projects.projection': 'Projection',
    'projects.total': 'Total projects',
    'projects.empty': 'No project has a cost this month.',
    'reconciliation.title': 'Reconciliation',
    'reconciliation.subtitle':
      '{{cycles}} cycles · incomplete: {{incomplete}} · longest cycle {{hours}} h',
    'reconciliation.subtitleNoLongest':
      '{{cycles}} cycles · incomplete: {{incomplete}}',
    'reconciliation.balanced': 'Total split = OVH total',
    'reconciliation.gap': 'Gap of {{amount}}',
    'reconciliation.day': 'Day',
    'reconciliation.cycles': 'Cycles',
    'reconciliation.incomplete': 'Incomplete',
    'reconciliation.ovh': 'OVH',
    'reconciliation.distributed': 'Split',
    'reconciliation.unattributed': 'Unattributed',
  },
});

/**
 * The French messages. Typographic apostrophe (’) and French quotes
 * throughout, as in the environments translation.
 */
const resource = createTranslationMessages({
  full: true,
  ref: projectCostsTranslationRef,
  messages: {
    'format.locale': 'fr-FR',
    'format.approx': '≈ {{amount}}',
    'card.title': 'Coûts',
    'card.subtitle': 'Coût réel du projet, réparti depuis la facture OVH',
    'card.months': 'Mois',
    'card.view': 'Vue des coûts',
    'card.viewMonth': 'Ce mois',
    'card.viewHistory': 'Historique',
    'card.empty': 'Pas encore de coûts pour ce projet',
    'card.emptyHint':
      'La collecte est horaire : les coûts apparaissent dès qu’un cycle de collecte a couvert le projet.',
    'card.error': 'Les coûts n’ont pas pu être lus.',
    'card.showDetails': 'Afficher le détail',
    'card.hideDetails': 'Masquer le détail',
    'fold.label': 'Chiffres clés du mois',
    'fold.toDate': 'à ce jour',
    'fold.ofMonth': 'du mois',
    'fold.projection': 'projection fin de mois',
    'fold.projected': 'projetés',
    'fold.days': 'par jour',
    'card.footnote':
      '« Plateforme » : part des coûts communs (cluster, répartiteurs de charge, outils, sauvegardes). « Capacité libre » : part inutilisée des nœuds du pool. Les deux sont inclus dans le coût complet, pas dans le coût direct.',
    'card.footnoteShort': 'Plateforme et capacité libre : parts communes',
    'card.footnoteMore': 'À propos des parts communes',
    'card.projectionNote': 'Projection fin de mois : {{method}}.',
    'status.open': 'Mois ouvert',
    'status.closed': 'Clos · rapproché de la facture',
    'status.historyOpen': 'Ouvert · provisoire',
    'status.openShort': 'ouvert',
    'status.closedShort': 'clos',
    'strip.label': 'Résumé du mois',
    'strip.complete': 'Coût complet à ce jour',
    'strip.completeClosed': 'Coût complet du mois',
    'strip.completeSub': 'dont direct {{direct}}',
    'strip.since': 'depuis le {{date}}',
    'strip.projection': 'Projection fin de mois',
    'strip.estimate': 'estimation',
    'strip.estimateShort': 'estim.',
    'strip.projectionShare':
      'part du projet {{share}} × prévision OVH {{forecast}}',
    'strip.projectionLinear': 'au prorata des heures couvertes',
    'strip.projectionClosed': 'Mois clos',
    'strip.coverage': 'Couverture',
    'strip.coverageValue': '{{covered}} h / {{total}} h',
    'strip.coverageSub': 'heures du mois déjà mesurées',
    'items.title': 'Par poste',
    'item.cpu': 'CPU',
    'item.ram': 'Mémoire',
    'item.volume': 'Volume',
    'item.nfs': 'Partage NFS',
    'item.s3': 'Stockage S3',
    'item.idle': 'Capacité libre',
    'item.platform': 'Plateforme',
    'items.others': 'Autres ({{names}})',
    'environments.titleOne': 'Environnement',
    'environments.title': 'Par environnement',
    'environments.only': 'seul environnement · {{amount}}',
    'environments.item': 'Poste',
    'environments.total': 'Total',
    'environments.complete': 'Complet',
    'days.title': 'Par jour',
    'days.label': 'Coût complet par jour, {{month}}',
    'days.bar': '{{day}} : {{amount}}',
    'days.error': 'Les coûts par jour n’ont pas pu être lus.',
    'history.label': 'Coût complet par mois',
    'history.month': 'Mois',
    'history.complete': 'Complet',
    'history.direct': 'Direct',
    'history.status': 'Statut',
    'history.error': 'L’historique n’a pas pu être lu.',
    'page.title': 'Coûts plateforme',
    'page.subtitle':
      'Coûts OVH de la plateforme, répartis par projet (administrateurs).',
    'page.months': 'Mois',
    'page.previousMonth': 'Mois précédent',
    'page.nextMonth': 'Mois suivant',
    'page.statusOpen': 'Mois ouvert',
    'page.statusOpenShort': 'ouvert',
    'page.statusClosed': 'Clos',
    'page.closeFrom': 'clôture possible à partir du {{date}}',
    'page.export': 'Exporter CSV',
    'page.error': 'Les coûts de la plateforme n’ont pas pu être lus.',
    'page.noMonth': 'Aucun mois n’a encore de coûts.',
    'kpi.ovhTotal': 'Total OVH du mois (cycles)',
    'kpi.ovhTotalSub': 'rapproché heure par heure',
    'kpi.distributed': 'Réparti sur les projets',
    'kpi.unattributed': 'Non attribué',
    'kpi.unattributedSub': '{{share}} · alerte au-delà de 2 %',
    'kpi.forecast': 'Prévision OVH fin de mois',
    'kpi.forecastSub': 'publiée par OVH (usage/forecast)',
    'kpi.coverage': 'Couverture',
    'kpi.coverageSub': 'collecte depuis le {{date}}',
    'projects.title': 'Projets',
    'projects.subtitle_one':
      '{{count}} projet · coût complet = direct + capacité libre + plateforme',
    'projects.subtitle_other':
      '{{count}} projets · coût complet = direct + capacité libre + plateforme',
    'projects.project': 'Projet',
    'projects.environments': 'Environnements',
    'projects.complete': 'Complet',
    'projects.direct': 'Direct',
    'projects.idle': 'Libre',
    'projects.platform': 'Plateforme',
    'projects.share': 'Part',
    'projects.projection': 'Projection',
    'projects.total': 'Total projets',
    'projects.empty': 'Aucun projet n’a de coût ce mois-ci.',
    'reconciliation.title': 'Rapprochement',
    'reconciliation.subtitle':
      '{{cycles}} cycles · incomplets : {{incomplete}} · cycle le plus long {{hours}} h',
    'reconciliation.subtitleNoLongest':
      '{{cycles}} cycles · incomplets : {{incomplete}}',
    'reconciliation.balanced': 'Total réparti = total OVH',
    'reconciliation.gap': 'Écart de {{amount}}',
    'reconciliation.day': 'Jour',
    'reconciliation.cycles': 'Cycles',
    'reconciliation.incomplete': 'Incomplets',
    'reconciliation.ovh': 'OVH',
    'reconciliation.distributed': 'Réparti',
    'reconciliation.unattributed': 'Non attribué',
  },
});

export const projectCostsFrTranslation = TranslationBlueprint.make({
  name: 'waadoo-project-costs-fr',
  params: { resource },
});
